/**
 * Arlo & Co — Custom Photo Uploader
 * Replaces the Uploadcare widget with a custom grid UI.
 * Uses Uploadcare's Upload API for CDN hosting, Cropper.js for cropping.
 *
 * Reads config from #arlo-uploader data attributes (set by Liquid).
 * Stores a Uploadcare group/file URL in the hidden input so cart works identically.
 *
 * Requires: Cropper.js loaded before this script.
 */

(function () {
  'use strict';

  // ── Step coordinator (shared with arlo-options.js and arlo-variant-buttons.js) ──
  if (!window._arloStepCoord) {
    window._arloStepCoord = {
      _badges: [],
      _scheduled: false,
      register: function (el) {
        this._badges.push(el);
        if (!this._scheduled) {
          this._scheduled = true;
          var self = this;
          setTimeout(function () {
            self._badges.sort(function (a, b) {
              return (a.compareDocumentPosition(b) & 4) ? -1 : 1;
            });
            self._badges.forEach(function (b, i) { b.textContent = i + 1; });
          }, 0);
        }
      }
    };
  }

  // ── State ────────────────────────────────────────────────────────────────────
  var cfg = {};
  var slots = [];        // [{ state:'empty'|'loading'|'done'|'error', uuid, cdnUrl, origFile }]
  var cropSlotIdx = null;
  var cropperInst = null;
  var _cropEnforcing = false; // re-entry guard for minimum size enforcement
  var cropBlobUrl = null;
  var bwActive = false;  // black & white toggle state
  var satActive = false; // saturation boost toggle state
  var liftActive = false;  // dark-photo brighten state
  var liftTouched = false; // customer pressed Brighten themselves — stop auto-switching

  // ── Upload persistence (2026-07-26) ─────────────────────────────────────────
  // Completed uploads live on the Uploadcare CDN — only the in-memory slot
  // list was lost when a customer accidentally navigated away (browser back
  // mid-upload of a 56-photo set = everything gone). Persist the done-slot
  // UUIDs per product page in sessionStorage after every change; restore on
  // load. Cleared automatically when resetAll() runs after add-to-cart
  // (savePersist sees no done slots and removes the key), and entries expire
  // after 24h. sessionStorage scope = this tab only, gone when tab closes.
  var PERSIST_TTL = 24 * 60 * 60 * 1000;
  var _restoredUuids = null; // full stored list, kept for variant resizeTo()
  function persistKey() { return 'arlo_uploads_' + location.pathname; }
  function savePersist() {
    try {
      var uuids = slots.map(function (s) { return s.state === 'done' ? (s.fullUuid || s.uuid) : null; });
      _restoredUuids = uuids;
      if (!uuids.some(Boolean)) { sessionStorage.removeItem(persistKey()); return; }
      sessionStorage.setItem(persistKey(), JSON.stringify({ t: Date.now(), uuids: uuids }));
    } catch (e) {}
  }
  function loadPersist() {
    try {
      var raw = sessionStorage.getItem(persistKey());
      if (!raw) return null;
      var d = JSON.parse(raw);
      if (!d || !d.uuids || (Date.now() - (d.t || 0)) > PERSIST_TTL) {
        sessionStorage.removeItem(persistKey());
        return null;
      }
      return d.uuids;
    } catch (e) { return null; }
  }
  function restoredSlot(uuid) {
    // origFile is gone after a reload — re-crop falls back to the CDN
    // '/-/original/' path, which openCropForSlot already handles.
    return { state: 'done', uuid: uuid, cdnUrl: 'https://ucarecdn.com/' + uuid + '/', origFile: null };
  }

  // Arch crop shape — flat-bottomed, round-topped "tombstone" outline.
  // Achieved with plain border-radius (elliptical corners) at a fixed aspect
  // ratio, same "CSS illusion" approach as circle — no real pixel masking.
  var ARCH_ASPECT = 0.81; // width / height — MUST match assets/custom.js and admin/public/index.html
  // Vertical radius = (ARCH_ASPECT / 2) * 100 = 40.5% — this makes the top corners a true
  // semicircle (not a squished ellipse) at the ARCH_ASPECT ratio. If ARCH_ASPECT ever
  // changes, recompute this the same way in every file that duplicates it (see below).
  var ARCH_RADIUS = '50% 50% 4px 4px / 40.5% 40.5% 4px 4px';

  // 'Decal Arch' — a second arch-style shape (same round-top/flat-sides/flat-bottom
  // "tombstone" outline, same CSS-radius technique) at a narrower/taller fixed ratio.
  // Aspect ratio 0.75 taken directly from the supplied reference artwork. MUST match
  // DECAL_ARCH_ASPECT in assets/custom.js and admin/public/index.html.
  var DECAL_ARCH_ASPECT = 0.75; // width / height
  var DECAL_ARCH_RADIUS = '50% 50% 4px 4px / 37.5% 37.5% 4px 4px'; // (0.75 / 2) * 100

  function isArchShape(shape) { return shape === 'arch' || shape === 'decal-arch'; }
  function archAspect(shape) { return shape === 'decal-arch' ? DECAL_ARCH_ASPECT : ARCH_ASPECT; }

  function buildCropFilter() {
    var parts = [];
    // Close match to liftPixelTable() on screen (a CSS filter can't do the exact curve)
    if (liftActive) parts.push('contrast(0.83) brightness(1.11)');
    if (bwActive) parts.push('grayscale(100%)');
    if (satActive) {
      // In B&W mode Vivid boosts contrast; in colour mode it boosts saturation
      parts.push(bwActive ? 'contrast(1.2)' : 'saturate(1.3)');
    }
    return parts.join(' ');
  }

  function applyLiveFilter() {
    var cropperContainer = document.querySelector('.cropper-container');
    if (cropperContainer) cropperContainer.style.filter = buildCropFilter();
  }

  // ── Dark photo brighten (2026-11-04) ────────────────────────────────────────
  // Admin per product ("Brighten dark photos"). Dark pets came out darker in print
  // than on a backlit screen. If the MIDDLE of the crop is mostly very dark (the
  // average misses a black dog in a bright garden), a light shadow lift is switched
  // on with a notice and a Brighten button to turn it off. Lifts shadows only, so
  // light areas barely change. DARK_LIFT 0.6 = "Medium" from Damian's print test on
  // Luxe and Bamboo, plus DARK_CONTRAST so the lifted shadows don't look flat.
  var DARK_LIFT = 0.6, DARK_CONTRAST = 1.08, DARK_LUMA = 50, DARK_TRIGGER = 0.35;
  function darkShareOfCrop() {
    if (!cropperInst) return 0;
    try {
      var c = cropperInst.getCroppedCanvas({ width: 96, height: 96, imageSmoothingQuality: 'low' });
      if (!c) return 0;
      var d = c.getContext('2d').getImageData(24, 24, 48, 48).data, dark = 0;
      for (var i = 0; i < d.length; i += 4) {
        if (0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2] < DARK_LUMA) dark++;
      }
      freeCanvas(c);
      return dark / (d.length / 4);
    } catch (e) { return 0; } // unreadable image: leave the photo alone
  }
  function renderLiftUi(show) {
    var btn = document.getElementById('arlo-lift-btn');
    var note = document.getElementById('arlo-crop-notice');
    if (btn) { btn.style.display = show ? '' : 'none'; btn.classList.toggle('active', liftActive); }
    if (note) {
      // Space stays reserved on these products: showing the notice later would
      // squeeze the crop area underneath the already laid-out cropper.
      note.style.display = cfg.darkLift ? 'block' : 'none';
      note.style.visibility = show ? 'visible' : 'hidden';
      note.textContent = liftActive
        ? 'This photo looks quite dark. Dark areas print darker than they look on screen, so we\'ve brightened it a little.'
        : 'This photo looks quite dark. Dark areas print darker than they look on screen.';
    }
  }
  function checkDarkCrop() {
    if (!cfg.darkLift || !cropperInst) return;
    var dark = darkShareOfCrop() >= DARK_TRIGGER;
    if (liftTouched) { renderLiftUi(true); return; }
    liftActive = dark;
    renderLiftUi(dark);
    applyLiveFilter();
  }
  var _darkTimer = null;
  function scheduleDarkCheck() {
    if (!cfg.darkLift) return;
    clearTimeout(_darkTimer);
    _darkTimer = setTimeout(checkDarkCrop, 250);
  }
  // Same curve as the print test (shadows up, highlights almost untouched), then a
  // slight contrast around mid-grey. Indexed by luma.
  function liftPixelTable(k, c) {
    var t = [];
    for (var i = 0; i < 256; i++) {
      var L = i / 255;
      var v = L + k * Math.pow(L, 0.6) * Math.pow(1 - L, 3);
      t.push(Math.max(0, Math.min(1, (v - 0.5) * c + 0.5)) * 255);
    }
    return t;
  }
  // Applies the crop window's effects to the file, in the preview's order.
  function applyPhotoEffects(canvas) {
    if (!liftActive && !bwActive && !satActive) return;
    var ctx = canvas.getContext('2d');
    var im = ctx.getImageData(0, 0, canvas.width, canvas.height), d = im.data;
    var table = liftActive ? liftPixelTable(DARK_LIFT, DARK_CONTRAST) : null;
    for (var i = 0; i < d.length; i += 4) {
      var r = d[i], g = d[i + 1], b = d[i + 2], y;
      if (table) {
        y = 0.299 * r + 0.587 * g + 0.114 * b;
        if (y >= 1) { var f = table[y | 0] / y; r *= f; g *= f; b *= f; }
      }
      if (bwActive) {
        r = g = b = 0.299 * r + 0.587 * g + 0.114 * b;
      }
      if (satActive) {
        if (bwActive) { // contrast(1.2)
          r = (r - 127.5) * 1.2 + 127.5; g = b = r;
        } else {        // saturate(1.3), the CSS filter's matrix
          var r0 = r, g0 = g, b0 = b;
          r = r0 * 1.2362 + g0 * -0.2146 + b0 * -0.0216;
          g = r0 * -0.0638 + g0 * 1.1354 + b0 * -0.0716;
          b = r0 * -0.0638 + g0 * -0.2146 + b0 * 1.2784;
        }
      }
      d[i] = r; d[i + 1] = g; d[i + 2] = b; // Uint8ClampedArray clamps + rounds
    }
    ctx.putImageData(im, 0, 0);
  }

  // ── CSS ──────────────────────────────────────────────────────────────────────
  var CSS = `
    .arlo-uploader { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; margin-bottom: 24px; }

    /* ── Step card header ── */
    .arlo-uploader__step-header {
      display: flex; align-items: center; gap: 12px;
      margin-bottom: 12px;
    }
    .arlo-uploader__step-badge {
      width: 28px; height: 28px; border-radius: 50%; flex-shrink: 0;
      background: var(--color-button-primary-background, #111);
      color: var(--color-button-primary-text, #fff);
      font-size: 13px; font-weight: 700;
      display: flex; align-items: center; justify-content: center;
    }
    .arlo-uploader__step-info { display: flex; flex-direction: column; gap: 1px; }
    .arlo-uploader__step-title {
      font-size: 12px; font-weight: 700; letter-spacing: 0.08em;
      text-transform: uppercase; color: var(--color-foreground, #111);
    }
    .arlo-uploader__step-sub {
      font-size: 11px; color: var(--color-foreground-secondary, #888);
    }

    .arlo-uploader__grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
      gap: 8px;
    }
    .arlo-uploader--single .arlo-uploader__grid {
      grid-template-columns: 1fr;
      max-width: 320px;
      margin-left: auto;
      margin-right: auto;
    }

    /* ── Slot base ── */
    .arlo-slot {
      aspect-ratio: var(--arlo-slot-ratio, 1);
      border-radius: 8px;
      overflow: hidden;
      position: relative;
      cursor: pointer;
      transition: border-color 0.15s, box-shadow 0.15s;
    }
    /* ── Circle crop mode ── */
    .arlo-uploader--circle .arlo-slot { border-radius: 50%; }
    /* ── Arch crop mode ── */
    .arlo-uploader--arch .arlo-slot { border-radius: ${ARCH_RADIUS}; }
    /* ── Decal Arch crop mode ── */
    .arlo-uploader--decal-arch .arlo-slot { border-radius: ${DECAL_ARCH_RADIUS}; }

    /* ── Empty slot ── */
    .arlo-slot--empty {
      border: 2px dashed #d0ccc8;
      background: #faf9f7;
      display: flex; align-items: center; justify-content: center;
    }
    .arlo-slot--empty:hover { border-color: #888; background: #f0eeeb; }
    .arlo-slot__empty-inner {
      display: flex; flex-direction: column; align-items: center; gap: 4px;
      color: #999; pointer-events: none; padding: 8px; text-align: center;
    }
    .arlo-slot__empty-inner svg { width: 22px; height: 22px; opacity: 0.5; flex-shrink: 0; }
    .arlo-slot__empty-label {
      font-size: 10px; font-weight: 600; letter-spacing: 0.04em;
      text-transform: uppercase; color: var(--color-foreground, #111);
      line-height: 1.2;
    }
    .arlo-slot__empty-hint {
      font-size: 9px; color: #aaa; letter-spacing: 0.02em; line-height: 1.2;
    }
    /* In circle mode the inscribed area is narrower — add extra side padding */
    .arlo-uploader--circle .arlo-slot__empty-inner { padding: 20px 14px; }
    /* In arch mode the rounded top narrows the inscribed area up top */
    .arlo-uploader--arch .arlo-slot__empty-inner { padding: 28px 14px 12px; }

    /* ── Loading slot ── */
    .arlo-slot--loading {
      border: 2px solid #e0deda;
      background: #faf9f7;
      display: flex; align-items: center; justify-content: center;
    }
    .arlo-slot__upload-overlay {
      position: absolute; inset: 0; z-index: 4;
      display: flex; align-items: center; justify-content: center;
      background: rgba(255,255,255,0.45);
    }
    .arlo-slot__spinner {
      width: 24px; height: 24px; border-radius: 50%;
      border: 3px solid #e0deda;
      border-top-color: #444;
      animation: arlo-spin 0.7s linear infinite;
    }
    @keyframes arlo-spin { to { transform: rotate(360deg); } }

    /* ── Done slot ── */
    .arlo-slot--done {
      border: 2px solid var(--color-button-primary-background, #111);
      background: #e8e8e8;
    }
    .arlo-slot--done:hover .arlo-slot__actions { opacity: 1; }
    .arlo-slot__thumb {
      position: absolute; inset: 0;
      width: 100%; height: 100%; object-fit: cover; display: block;
      transition: opacity 0.15s;
    }
    .arlo-slot--done:hover .arlo-slot__thumb { opacity: 0.6; }
    /* Proof overlay — sits on top of the thumbnail (and crop box) so the customer
       always sees the product cut-out / safe zone. Never intercepts clicks. */
    .arlo-slot__overlay {
      position: absolute; inset: 0;
      width: 100%; height: 100%; object-fit: fill; display: block;
      pointer-events: none; z-index: 2;
    }
    .arlo-crop-overlay-img {
      position: absolute; inset: 0;
      width: 100%; height: 100%; object-fit: fill;
      pointer-events: none; z-index: 5;
    }
    .arlo-slot__actions {
      position: absolute; inset: 0; z-index: 4;
      display: flex; align-items: center; justify-content: center; gap: 8px;
      opacity: 0; transition: opacity 0.15s;
    }
    .arlo-slot__btn {
      width: 32px; height: 32px; border-radius: 50%;
      border: none; cursor: pointer; font-size: 14px;
      display: flex; align-items: center; justify-content: center;
      backdrop-filter: blur(4px);
    }
    .arlo-slot__btn--remove { background: rgba(220,53,69,0.9); color: #fff; }
    .arlo-slot__btn--crop   { background: rgba(255,255,255,0.9); color: #222; }

    /* ── Error slot ── */
    .arlo-slot--error {
      border: 2px solid #dc3545;
      background: #fff5f5;
      display: flex; align-items: center; justify-content: center;
    }
    .arlo-slot__err-inner {
      font-size: 10px; color: #dc3545; text-align: center; padding: 4px;
    }

    /* ── Error message ── */
    .arlo-uploader__msg {
      margin-top: 10px; font-size: 12px; color: #dc3545;
      display: none;
    }

    /* ── Crop modal ── */
    .arlo-crop-overlay {
      position: fixed; inset: 0; z-index: 9999;
      display: flex; align-items: stretch;
      background: #fff; /* mobile: white full-screen */
    }
    .arlo-crop-card {
      display: flex; flex-direction: column;
      width: 100%; background: #fff;
    }
    .arlo-crop-topbar {
      display: flex; align-items: center; justify-content: space-between;
      padding: 12px 16px; flex-shrink: 0;
      background: #fff; border-bottom: 1px solid #e5e5e5;
    }
    .arlo-crop-topbar-title {
      color: #111; font-size: 15px; font-weight: 600; letter-spacing: 0.02em;
      text-transform: uppercase;
    }
    .arlo-crop-close {
      background: none; border: 1px solid #ccc; cursor: pointer;
      padding: 4px 10px; color: #444; font-size: 11px; font-weight: 600;
      letter-spacing: 0.06em; text-transform: uppercase;
    }
    .arlo-crop-close:hover { background: #f0f0f0; }
    .arlo-crop-body {
      flex: 1; overflow: hidden; position: relative; min-height: 0;
      background: #fff;
    }
    .arlo-crop-body img { display: block; max-width: 100%; max-height: 100%; }
    .arlo-crop-body .cropper-modal { opacity: 0.65 !important; }
    .cropper-crop-box--circle .cropper-view-box,
    .cropper-crop-box--circle .cropper-face { border-radius: 50% !important; }
    .cropper-crop-box--arch .cropper-view-box,
    .cropper-crop-box--arch .cropper-face { border-radius: ${ARCH_RADIUS} !important; }
    .cropper-crop-box--decal-arch .cropper-view-box,
    .cropper-crop-box--decal-arch .cropper-face { border-radius: ${DECAL_ARCH_RADIUS} !important; }
    .arlo-crop-footer {
      background: #fff; padding: 12px 16px;
      padding-bottom: calc(48px + env(safe-area-inset-bottom, 0px));
      border-top: 1px solid #e5e5e5;
      display: flex; gap: 10px; flex-shrink: 0;
    }
    .arlo-crop-btn {
      flex: 1; padding: 13px 16px; border-radius: 0; border: none;
      font-size: 13px; font-weight: 600; cursor: pointer;
      letter-spacing: 0.08em; text-transform: uppercase;
      transition: opacity 0.15s;
    }
    .arlo-crop-btn:hover { opacity: 0.85; }
    .arlo-crop-btn--cancel { background: #f0f0f0; color: #444; border: 1px solid #ccc; }
    .arlo-crop-btn--done   { background: var(--color-button-primary-background, #1c3a52); color: var(--color-button-primary-text, #fff); }
    .arlo-crop-btn--rotate { flex: 0 0 48px; background: #f0f0f0; color: #444; border: 1px solid #ccc; font-size: 18px; padding: 13px 0; }
    .arlo-crop-btn--bw { flex: 0 0 auto; background: #f0f0f0; color: #444; border: 1px solid #ccc; font-size: 11px; font-weight: 700; letter-spacing: 0.04em; padding: 13px 10px; }
    .arlo-crop-btn--bw.active { background: #222; color: #fff; border-color: #222; }
    @media (max-width: 600px) {
      .arlo-crop-footer--lift { flex-wrap: wrap; }
      .arlo-crop-footer--lift .arlo-crop-btn--done { flex: 1 1 100%; }
      .arlo-crop-footer--lift .arlo-crop-btn--bw { flex: 1 1 auto; }
    }
    #arlo-crop-notice { display: none; flex: 0 0 auto; min-height: 46px; box-sizing: border-box; background: #fff6e0; color: #5c4400; font-size: 12px; line-height: 1.35; text-align: center; padding: 7px 14px; font-family: -apple-system, sans-serif; }
    /* ── Mobile landscape: full-screen, topbar hidden, tools on RHS ── */
    @media (max-width: 767px) and (orientation: landscape) {
      .arlo-crop-card {
        position: relative;
        display: grid;
        grid-template-areas: "body footer";
        grid-template-columns: 1fr 72px;
        grid-template-rows: 1fr;
        height: 100%;
      }
      .arlo-crop-topbar { display: none; }
      /* Not a grid cell here: lies over the bottom of the photo, beside the tools */
      #arlo-crop-notice { position: absolute; left: 0; right: 72px; bottom: 0; z-index: 5; min-height: 0; padding: 4px 10px; font-size: 11px; opacity: 0.94; }
      .arlo-crop-body {
        grid-area: body;
        height: 100%;
      }
      .arlo-crop-footer {
        grid-area: footer;
        flex-direction: column;
        border-top: none;
        border-left: 1px solid #e5e5e5;
        padding: 48px 6px 10px;
        padding-right: calc(6px + env(safe-area-inset-right, 0px));
        padding-bottom: calc(10px + env(safe-area-inset-bottom, 0px));
        gap: 8px;
        overflow-y: auto;
      }
      .arlo-crop-btn {
        padding: 10px 4px;
        font-size: 10px;
        flex: 0 0 auto;
      }
      .arlo-crop-btn--done {
        white-space: normal;
        text-align: center;
        line-height: 1.3;
        flex: 1;
        min-height: 48px;
      }
      .arlo-crop-btn--rotate { font-size: 20px; padding: 10px 0; }
      .arlo-crop-landscape-close { display: flex !important; }
    }
    .arlo-crop-landscape-close {
      display: none;
      position: absolute;
      top: 8px; left: 8px; z-index: 10;
      width: 32px; height: 32px;
      align-items: center; justify-content: center;
      background: rgba(0,0,0,0.45); border: none; border-radius: 50%;
      color: #fff; font-size: 18px; cursor: pointer; line-height: 1;
    }
    /* ── Desktop: centred card over dark backdrop ── */
    @media (min-width: 768px) {
      .arlo-crop-overlay {
        background: rgba(0,0,0,0.72);
        align-items: center; justify-content: center;
        padding: 24px;
      }
      .arlo-crop-card {
        width: 100%; max-width: 860px;
        max-height: 90vh;
        box-shadow: 0 24px 64px rgba(0,0,0,0.35);
      }
      .arlo-crop-body { max-height: calc(90vh - 110px); }
    }
  `;

  function injectStyles() {
    if (document.getElementById('arlo-uploader-styles')) return;
    var s = document.createElement('style');
    s.id = 'arlo-uploader-styles';
    s.textContent = CSS;
    document.head.appendChild(s);
  }

  // ── Render ───────────────────────────────────────────────────────────────────
  function mount(root) {
    injectStyles();
    root.innerHTML = '';
    root.className = 'arlo-uploader'
      + (cfg.circle ? ' arlo-uploader--circle' : '')
      + (isArchShape(cfg.shape) ? ' arlo-uploader--' + cfg.shape : '')
      + (cfg.maxImages === 1 ? ' arlo-uploader--single' : '');

    // Set slot aspect ratio from crop config (e.g. "3:2" → 1.5, "1181x591" → 2, circle → 1, arch/decal-arch → their fixed aspect)
    var slotRatio = cfg.circle ? 1 : (isArchShape(cfg.shape) ? archAspect(cfg.shape) : (parseCropRatio(cfg.crop) || 1));
    root.style.setProperty('--arlo-slot-ratio', slotRatio);

    // Step card header — badge number assigned by coordinator
    var stepHeader = document.createElement('div');
    stepHeader.className = 'arlo-uploader__step-header';
    var uploaderBadge = document.createElement('div');
    uploaderBadge.className = 'arlo-uploader__step-badge';
    uploaderBadge.textContent = '';
    if (window._arloStepCoord) window._arloStepCoord.register(uploaderBadge);
    var uploaderInfo = document.createElement('div');
    uploaderInfo.className = 'arlo-uploader__step-info';
    uploaderInfo.innerHTML =
        '<div class="arlo-uploader__step-title">Upload your photo' + (cfg.maxImages > 1 ? 's' : '') + '</div>'
      + '<div class="arlo-uploader__step-sub" id="arlo-count">Add ' + (cfg.maxImages === 1 ? 'your photo' : cfg.maxImages + ' photos') + ' to continue</div>';
    stepHeader.appendChild(uploaderBadge);
    stepHeader.appendChild(uploaderInfo);
    root.appendChild(stepHeader);

    // Grid
    var grid = document.createElement('div');
    grid.className = 'arlo-uploader__grid';
    grid.id = 'arlo-grid';
    root.appendChild(grid);

    // Error msg
    var msg = document.createElement('div');
    msg.className = 'arlo-uploader__msg';
    msg.id = 'arlo-msg';
    root.appendChild(msg);

    // Hidden file input
    var fi = document.createElement('input');
    fi.type = 'file';
    fi.accept = 'image/*';
    fi.style.display = 'none';
    fi.id = 'arlo-file-input';
    fi.addEventListener('change', onFileChosen);
    root.appendChild(fi);

    // Crop modal (hidden initially)
    var modal = buildCropModal();
    modal.id = 'arlo-crop-modal';
    modal.style.display = 'none';
    document.body.appendChild(modal);

    renderGrid();
    updateCount();
  }

  function renderGrid() {
    var grid = document.getElementById('arlo-grid');
    if (!grid) return;
    grid.innerHTML = '';
    slots.forEach(function (slot, i) {
      grid.appendChild(makeSlotEl(slot, i));
    });
  }

  function makeSlotEl(slot, i) {
    var el = document.createElement('div');
    el.className = 'arlo-slot arlo-slot--' + slot.state;
    el.dataset.idx = i;

    // Proof overlay markup (transparent PNG on top of the thumbnail), if configured.
    var ovHtml = cfg.overlay ? '<img class="arlo-slot__overlay" src="' + cfg.overlay + '" alt="" aria-hidden="true">' : '';

    if (slot.state === 'empty') {
      el.innerHTML = '<div class="arlo-slot__empty-inner">'
        + '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">'
        + '<path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/>'
        + '<circle cx="12" cy="13" r="4"/></svg>'
        + '<span class="arlo-slot__empty-label">Tap to upload</span>'
        + '<span class="arlo-slot__empty-hint">JPG · PNG · HEIC</span>'
        + '</div>';
      el.addEventListener('click', function () { triggerPicker(i); });

    } else if (slot.state === 'loading') {
      if (slot.previewUrl) {
        // Show cropped preview immediately while uploading — spinner overlaid on top
        el.innerHTML = '<img class="arlo-slot__thumb" src="' + slot.previewUrl + '" alt="">'
          + ovHtml
          + '<div class="arlo-slot__upload-overlay"><div class="arlo-slot__spinner"></div></div>';
      } else {
        el.innerHTML = '<div class="arlo-slot__spinner"></div>';
      }

    } else if (slot.state === 'done') {
      el.innerHTML = '<img class="arlo-slot__thumb" src="' + slotThumbUrl(slot) + '" alt="Photo ' + (i + 1) + '">'
        + ovHtml
        + '<div class="arlo-slot__actions">'
        + '<button type="button" class="arlo-slot__btn arlo-slot__btn--crop" title="Re-crop">✎</button>'
        + '<button type="button" class="arlo-slot__btn arlo-slot__btn--remove" title="Remove">×</button>'
        + '</div>';
      el.querySelector('.arlo-slot__btn--remove').addEventListener('click', function (e) { e.stopPropagation(); clearSlot(i); });
      el.querySelector('.arlo-slot__btn--crop').addEventListener('click', function (e)   { e.stopPropagation(); openCropForSlot(i, null); });

    } else if (slot.state === 'error') {
      el.innerHTML = '<div class="arlo-slot__err-inner">Upload failed<br><small>Tap to retry</small></div>';
      el.addEventListener('click', function () { triggerPicker(i); });
    }

    return el;
  }

  function refreshSlot(i) {
    var grid = document.getElementById('arlo-grid');
    if (!grid) return;
    var old = grid.querySelector('[data-idx="' + i + '"]');
    if (old) grid.replaceChild(makeSlotEl(slots[i], i), old);
    updateCount();
  }

  function updateCount() {
    var el = document.getElementById('arlo-count');
    if (!el) return;
    var done = slots.filter(function (s) { return s.state === 'done'; }).length;
    var total = slots.length;
    if (done === 0) {
      el.textContent = total === 1 ? 'Add your photo to continue' : 'Add ' + total + ' photos to continue';
    } else if (done < total) {
      el.textContent = done + ' of ' + total + ' photos uploaded';
    } else {
      el.textContent = total === 1 ? '✓ Photo uploaded' : '✓ All ' + total + ' photos uploaded';
    }
    updateStickyBtn(done, total);
  }

  // ── Sticky ATC button label ───────────────────────────────────────────────────
  // Relabels the [data-add-to-cart-text] span inside ATC buttons to reflect
  // upload progress. Targets the inner span so the button's DOM structure
  // (which the theme JS depends on) stays intact.
  var _stickyOrigText = null;

  function updateStickyBtn(done, total) {
    var btns = document.querySelectorAll('[data-add-to-cart]');
    if (!btns.length) return;

    var ready = (total - done) === 0;

    // Dim/restore the button — text stays as-is
    btns.forEach(function (btn) {
      btn.style.opacity = ready ? '' : '0.6';
      btn.style.cursor  = ready ? '' : 'default';
    });
  }

  // ── File picker ──────────────────────────────────────────────────────────────
  function triggerPicker(slotIdx) {
    cropSlotIdx = slotIdx;
    var fi = document.getElementById('arlo-file-input');
    if (fi) { fi.value = ''; fi.click(); }
  }

  function onFileChosen(e) {
    var file = e.target.files[0];
    if (!file || cropSlotIdx === null) return;
    openCropForSlot(cropSlotIdx, file);
  }

  // ── Crop modal ───────────────────────────────────────────────────────────────
  function buildCropModal() {
    // Overlay = full-screen backdrop (dark on desktop, white on mobile)
    var overlay = document.createElement('div');
    overlay.className = 'arlo-crop-overlay';

    // Card = the white box centred inside the overlay on desktop
    var card = document.createElement('div');
    card.className = 'arlo-crop-card';

    // Top bar with title + close button
    var topbar = document.createElement('div');
    topbar.className = 'arlo-crop-topbar';
    var title = document.createElement('span');
    title.className = 'arlo-crop-topbar-title';
    title.textContent = 'Crop your photo';
    var closeBtn = document.createElement('button');
    closeBtn.className = 'arlo-crop-close';
    closeBtn.textContent = 'Close';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.addEventListener('click', closeCropModal);
    topbar.appendChild(title);
    topbar.appendChild(closeBtn);
    card.appendChild(topbar);

    var body = document.createElement('div');
    body.className = 'arlo-crop-body';
    var img = document.createElement('img');
    img.id = 'arlo-crop-img';
    body.appendChild(img);
    card.appendChild(body);

    var modalMsg = document.createElement('div');
    modalMsg.id = 'arlo-crop-msg';
    modalMsg.style.cssText = 'display:none;color:#c0392b;font-size:12px;text-align:center;padding:6px 16px 0;font-family:-apple-system,sans-serif';
    card.appendChild(modalMsg);

    var darkNotice = document.createElement('div');
    darkNotice.id = 'arlo-crop-notice';
    card.appendChild(darkNotice);

    var footer = document.createElement('div');
    // Products with Brighten have a 5th button: on a phone "Use this photo" gets its own
    // row (decided up front — wrapping later would squeeze the laid-out cropper).
    footer.className = 'arlo-crop-footer' + (cfg.darkLift ? ' arlo-crop-footer--lift' : '');

    var rotL = document.createElement('button');
    rotL.type = 'button';
    rotL.className = 'arlo-crop-btn arlo-crop-btn--rotate';
    rotL.title = 'Rotate left';
    rotL.innerHTML = '↺';
    rotL.addEventListener('click', function () { if (cropperInst) cropperInst.rotate(-90); });

    var rotR = document.createElement('button');
    rotR.type = 'button';
    rotR.className = 'arlo-crop-btn arlo-crop-btn--rotate';
    rotR.title = 'Rotate right';
    rotR.innerHTML = '↻';
    rotR.addEventListener('click', function () { if (cropperInst) cropperInst.rotate(90); });

    var done = document.createElement('button');
    done.type = 'button'; // must be explicit — default 'submit' would submit the product form
    done.className = 'arlo-crop-btn arlo-crop-btn--done';
    done.textContent = '✓ Use this photo';
    done.addEventListener('click', onCropDone);

    var bwBtn = document.createElement('button');
    bwBtn.type = 'button';
    bwBtn.className = 'arlo-crop-btn arlo-crop-btn--bw';
    bwBtn.id = 'arlo-bw-btn';
    bwBtn.title = 'Convert to black & white';
    bwBtn.textContent = 'B&W';
    bwBtn.addEventListener('click', function () {
      bwActive = !bwActive;
      bwBtn.classList.toggle('active', bwActive);
      applyLiveFilter();
    });

    var satBtn = document.createElement('button');
    satBtn.type = 'button';
    satBtn.className = 'arlo-crop-btn arlo-crop-btn--bw';
    satBtn.id = 'arlo-sat-btn';
    satBtn.title = 'Boost colour saturation';
    satBtn.textContent = 'Vivid';
    satBtn.addEventListener('click', function () {
      satActive = !satActive;
      satBtn.classList.toggle('active', satActive);
      applyLiveFilter();
    });

    var liftBtn = document.createElement('button');
    liftBtn.type = 'button';
    liftBtn.className = 'arlo-crop-btn arlo-crop-btn--bw';
    liftBtn.id = 'arlo-lift-btn';
    liftBtn.title = 'Brighten dark areas';
    liftBtn.textContent = 'Brighten';
    liftBtn.style.display = 'none';
    liftBtn.addEventListener('click', function () {
      liftTouched = true;
      liftActive = !liftActive;
      renderLiftUi(true);
      applyLiveFilter();
    });

    footer.appendChild(rotL);
    footer.appendChild(rotR);
    footer.appendChild(bwBtn);
    footer.appendChild(satBtn);
    footer.appendChild(liftBtn);
    footer.appendChild(done);
    card.appendChild(footer);

    // Landscape-only × close button (top-left, shown via CSS in landscape)
    var lsClose = document.createElement('button');
    lsClose.type = 'button';
    lsClose.className = 'arlo-crop-landscape-close';
    lsClose.setAttribute('aria-label', 'Close');
    lsClose.innerHTML = '&times;';
    lsClose.addEventListener('click', closeCropModal);
    card.appendChild(lsClose);

    overlay.appendChild(card);
    return overlay;
  }

  // Overlay the proof PNG on top of the Cropper crop box so the customer can see
  // the product cut-out / safe zone while positioning. Runs in the Cropper ready
  // callback; pointer-events:none keeps drag/zoom working.
  function addCropOverlay() {
    if (!cfg.overlay || !cropperInst || !cropperInst.cropper) return;
    var box = cropperInst.cropper.querySelector('.cropper-crop-box');
    if (!box || box.querySelector('.arlo-crop-overlay-img')) return;
    var ovi = document.createElement('img');
    ovi.className = 'arlo-crop-overlay-img';
    ovi.src = cfg.overlay;
    ovi.alt = '';
    ovi.setAttribute('aria-hidden', 'true');
    box.appendChild(ovi);
  }

  function openCropForSlot(slotIdx, file) {
    cropSlotIdx = slotIdx;
    var modal = document.getElementById('arlo-crop-modal');
    if (!modal) return;

    // Revoke old blob URL
    if (cropBlobUrl) { URL.revokeObjectURL(cropBlobUrl); cropBlobUrl = null; }

    // Source: new file, or the slot's original file blob, or fallback to CDN url (re-crop)
    var src;
    if (file) {
      cropBlobUrl = URL.createObjectURL(file);
      src = cropBlobUrl;
      slots[slotIdx].origFile = file;
      // Large prints: upload the untouched original in the background while the
      // customer crops (see "Full-resolution uploads" below).
      if (cfg.fullRes) slots[slotIdx].origUp = startOriginalUpload(file);
    } else if (slots[slotIdx].origFile) {
      cropBlobUrl = URL.createObjectURL(slots[slotIdx].origFile);
      src = cropBlobUrl;
    } else if (slots[slotIdx].cdnUrl) {
      // Re-crop from CDN — use original (no transforms) via /-/original/
      src = slots[slotIdx].cdnUrl.replace(/\/-\/.*$/, '') + '/-/original/';
    } else {
      return;
    }

    var img = document.getElementById('arlo-crop-img');

    // Destroy old Cropper
    if (cropperInst) { cropperInst.destroy(); cropperInst = null; }

    modal.style.display = 'flex';

    // On orientation change: debounced destroy + reinit so layout settles first.
    // Only listen to orientationchange — resize fires too many times and stacks instances.
    var _orientTimeout;
    function onOrientationChange() {
      clearTimeout(_orientTimeout);
      _orientTimeout = setTimeout(function () {
        var img2 = document.getElementById('arlo-crop-img');
        if (!img2 || !img2.src) return;
        if (cropperInst) { cropperInst.destroy(); cropperInst = null; }
        setTimeout(function () {
          var ar2 = parseCropRatio(cfg.crop);
          cropperInst = new Cropper(img2, {
            aspectRatio: cfg.circle ? 1 : (isArchShape(cfg.shape) ? archAspect(cfg.shape) : ar2),
            viewMode: 1,
            autoCropArea: 0.95,
            responsive: true,
            checkCrossOrigin: false,
            background: false,
            ready: function () {
              if (cfg.circle) {
                var box = cropperInst.cropper.querySelector('.cropper-crop-box');
                if (box) box.classList.add('cropper-crop-box--circle');
              } else if (isArchShape(cfg.shape)) {
                var box2 = cropperInst.cropper.querySelector('.cropper-crop-box');
                if (box2) box2.classList.add('cropper-crop-box--' + cfg.shape);
              }
              addCropOverlay();
              scheduleDarkCheck();
            },
            cropend: scheduleDarkCheck,
            zoom: scheduleDarkCheck
          });
        }, 100);
      }, 400);
    }
    window.addEventListener('orientationchange', onOrientationChange);
    modal._cleanupOrient = function () {
      window.removeEventListener('orientationchange', onOrientationChange);
      clearTimeout(_orientTimeout);
    };

    // Reset effect states on each open
    bwActive = false;
    satActive = false;
    liftActive = false;
    liftTouched = false;
    renderLiftUi(false);
    var bwBtn = document.getElementById('arlo-bw-btn');
    if (bwBtn) bwBtn.classList.remove('active');
    var satBtn = document.getElementById('arlo-sat-btn');
    if (satBtn) satBtn.classList.remove('active');
    applyLiveFilter();

    // Parse crop ratio from config  e.g. "1181x1181" → aspectRatio 1
    var ar = parseCropRatio(cfg.crop);

    // Set onload BEFORE src to avoid race condition where image loads before handler is attached
    img.onload = function () {
      if (typeof Cropper === 'undefined') {
        console.warn('[ArloUploader] Cropper.js not loaded');
        return;
      }
      cropperInst = new Cropper(img, {
        aspectRatio: cfg.circle ? 1 : (isArchShape(cfg.shape) ? archAspect(cfg.shape) : ar),
        viewMode: 1,
        autoCropArea: 0.95,
        responsive: true,
        checkCrossOrigin: false,
        background: false,
        ready: function() {
          if (cfg.circle) {
            var box = cropperInst.cropper.querySelector('.cropper-crop-box');
            if (box) box.classList.add('cropper-crop-box--circle');
          } else if (isArchShape(cfg.shape)) {
            var box2 = cropperInst.cropper.querySelector('.cropper-crop-box');
            if (box2) box2.classList.add('cropper-crop-box--' + cfg.shape);
          }
          addCropOverlay();
          scheduleDarkCheck();
        },
        cropend: scheduleDarkCheck,
        zoom: scheduleDarkCheck,
        // No real-time minimum enforcement — it locks the crop box and prevents repositioning.
        // Minimum size is checked at submit time in onCropDone() instead.
      });
    };

    // Set src after onload is wired up
    img.src = src;
  }

  function closeCropModal() {
    var modal = document.getElementById('arlo-crop-modal');
    if (modal) {
      modal.style.display = 'none';
      if (modal._cleanupOrient) { modal._cleanupOrient(); modal._cleanupOrient = null; }
    }
    if (cropperInst) { cropperInst.destroy(); cropperInst = null; }
    if (cropBlobUrl) { URL.revokeObjectURL(cropBlobUrl); cropBlobUrl = null; }
  }

  // Longest side of the uploaded photo, in px: the product's print size at
  // OUTPUT_DPI, never below the product's own minimum (minPx), never above
  // OUTPUT_MAX_PX (and croppedCanvas also keeps it under OUTPUT_MAX_AREA).
  // Photos are never enlarged — a smaller original comes through at its own size.
  // 2026-10-14 (Damian): large products were capped at 2625px (40x60cm decals got
  // ~96 dpi from 24MB originals), small ones got more than they need. Now:
  // 450 dpi target (still 1.5x print), long side up to 4096px, and at most
  // OUTPUT_MAX_AREA pixels in total — iPhones' hard canvas limit is ~16.7MP and
  // going near it is what produced the blank white files, so 12MP keeps headroom
  // (isBlankCanvas + smaller retry stay as the safety net).
  var OUTPUT_DPI = 450, OUTPUT_MAX_PX = 4096, OUTPUT_MAX_AREA = 12000000;
  function outputMaxPx() {
    var need = 0;
    var mm = (cfg.printMm || '').split('x').map(parseFloat);
    if (mm[0] > 0 && mm[1] > 0) need = Math.round(Math.max(mm[0], mm[1]) / 25.4 * OUTPUT_DPI);
    var min = (cfg.minPx || '').split('x').map(function (n) { return parseInt(n, 10) || 0; });
    need = Math.max(need, min[0] || 0, min[1] || 0);
    return need > 0 ? Math.min(need, OUTPUT_MAX_PX) : OUTPUT_MAX_PX;
  }

  // Explicit output width/height from the crop's real pixel size, scaled down
  // (never up) so its longest side is maxPx. Not maxWidth/maxHeight — Cropper
  // applies those using the photo's aspect, which silently shrank output.
  function croppedCanvas(maxPx) {
    var d = cropperInst.getData(true); // crop area in the original photo's pixels
    var long = Math.max(d.width, d.height) || maxPx;
    var area = (d.width * d.height) || 1;
    var s = Math.min(1, maxPx / long, Math.sqrt(OUTPUT_MAX_AREA / area));
    return cropperInst.getCroppedCanvas({
      width: Math.max(1, Math.round(d.width * s)),
      height: Math.max(1, Math.round(d.height * s)),
      fillColor: '#fff', imageSmoothingEnabled: true, imageSmoothingQuality: 'high'
    });
  }

  // True when the canvas is one flat colour (what iOS hands back when it runs
  // out of canvas memory: our white fill and nothing drawn on it). Checks a
  // 32×32 downscaled copy — a few ms. A genuine one-colour photo is near
  // impossible; if the drawn image failed, every sample is identical.
  function isBlankCanvas(canvas) {
    try {
      var s = document.createElement('canvas');
      s.width = s.height = 32;
      var ctx = s.getContext('2d');
      ctx.drawImage(canvas, 0, 0, 32, 32);
      var d = ctx.getImageData(0, 0, 32, 32).data;
      var r = d[0], g = d[1], b = d[2], flat = true;
      for (var i = 4; i < d.length; i += 4) {
        if (Math.abs(d[i] - r) > 2 || Math.abs(d[i + 1] - g) > 2 || Math.abs(d[i + 2] - b) > 2) { flat = false; break; }
      }
      freeCanvas(s);
      return flat;
    } catch (e) {
      return false; // can't read pixels (shouldn't happen) — don't block the upload
    }
  }

  function freeCanvas(c) {
    if (c) { c.width = 0; c.height = 0; }
  }

  function onCropDone() {
    console.log('[ArloUploader] onCropDone called, cropperInst=', cropperInst, 'cropSlotIdx=', cropSlotIdx);
    if (!cropperInst) {
      console.error('[ArloUploader] cropperInst is null — Cropper.js may not have initialised');
      showMsg('Error: crop tool not ready. Try closing and re-opening the crop window.');
      return;
    }

    // ── Minimum resolution guard (only if enforceMinPx is enabled for this product) ──
    if (cfg.enforceMinPx && cfg.minPx) {
      var minParts = cfg.minPx.split('x');
      var minW = parseInt(minParts[0]), minH = parseInt(minParts[1]);
      if (minW && minH) {
        var d = cropperInst.getData(true);
        console.log('[ArloUploader] crop data', d, 'min', minW, minH);
        if (d.width < minW || d.height < minH) {
          var mm = document.getElementById('arlo-crop-msg');
          if (mm) { mm.textContent = 'Your image is too small. Please choose a higher quality photo.'; mm.style.display = 'block'; }
          return;
        }
      }
    }

    // Output size (2026-10-07): sized for PRINT, not the photo's full size —
    // see outputMaxPx(). Before this every crop was made at up to 3500px from the
    // customer's original, and on iPhones those big canvases sometimes came back
    // blank (white) without any error: ~0.9% of uploads from 17 Sep 2026, when
    // the cap went up. Smaller canvases make that rare; isBlankCanvas() catches
    // the rest and retries smaller before anything is uploaded.
    // Full-resolution copy (large prints): remember exactly what was cropped so
    // Uploadcare can cut the same area from the original. The normal crop below
    // is still made and uploaded first — it's the backup.
    var fullCrop = null;
    // (Uploadcare can't do the brighten curve, so a brightened photo keeps the normal crop.)
    if (cfg.fullRes && !liftActive && slots[cropSlotIdx] && slots[cropSlotIdx].origUp) {
      var fd0 = cropperInst.getData(true);
      fullCrop = { x: fd0.x, y: fd0.y, w: fd0.width, h: fd0.height, rotate: fd0.rotate || 0,
                   bw: bwActive, vivid: satActive, origUp: slots[cropSlotIdx].origUp };
    }

    var cap = outputMaxPx();
    var canvas = croppedCanvas(cap);
    if (!canvas) {
      showMsg('Error: could not read crop area. Please try again.');
      return;
    }
    if (isBlankCanvas(canvas)) {
      console.warn('[ArloUploader] blank crop at', cap, 'px — retrying smaller');
      freeCanvas(canvas);
      canvas = croppedCanvas(Math.max(600, Math.round(cap * 0.6)));
      if (!canvas || isBlankCanvas(canvas)) {
        freeCanvas(canvas);
        showMsg('Sorry, that photo didn\'t process properly. Please tap Use this photo again, or choose the photo again.');
        return;
      }
    }
    // Pixel maths, not ctx.filter (unsupported on older iOS Safari). 2026-11-04: Vivid
    // now reaches the file too — it only ever changed the preview here.
    applyPhotoEffects(canvas);
    var wasLifted = liftActive;
    canvas.toBlob(function (blob) {
      console.log('[ArloUploader] blob size=', blob && blob.size, 'px=', canvas.width + 'x' + canvas.height);
      freeCanvas(canvas); // iOS frees canvas memory late — release it now (decal sets make dozens)
      if (!blob) { showMsg('Error: could not generate image. Please try again.'); return; }
      closeCropModal();
      uploadBlob(cropSlotIdx, blob, fullCrop, wasLifted);
    }, 'image/jpeg', 0.95);
  }

  // ── Full-resolution uploads for large prints (2026-10-14) ───────────────────
  // For products with cfg.fullRes (admin: "Full-resolution upload", Auto = 30cm+
  // prints) the customer's ORIGINAL photo is uploaded untouched, in the
  // background from the moment it's picked (chunked + retried for big files),
  // and Uploadcare cuts the exact same crop from it at full resolution as a NEW
  // stored file (from_url of the processed CDN URL). The group/properties then
  // hold plain image files, so the production end needs no change.
  // Safety: the normal phone-made crop is always uploaded first as a backup;
  // the full-res file only replaces it once it exists AND a tiny thumbnail
  // comparison confirms it shows the same picture (guards orientation issues).
  // Any failure or slowness → the backup stays. Never blocks for long.
  var FULLRES_CHUNK = 5242880;          // Uploadcare multipart part size
  var FULLRES_MULTIPART_MIN = 10485760; // multipart needs >= 10MB
  var FULLRES_WAIT_MS = 60000;          // max add-to-cart wait after the click
  var FULLRES_MAX_RESIZE = 5000;        // Uploadcare resize limit (needed for colour adjustments)

  function ucForm(fields) {
    var fd = new FormData();
    Object.keys(fields).forEach(function (k) { fd.append(k, fields[k]); });
    return fd;
  }
  function withRetry(fn, tries) {
    return fn().catch(function (err) {
      if (tries <= 1) throw err;
      return new Promise(function (r) { setTimeout(r, 1500 * (5 - tries)); }).then(function () { return withRetry(fn, tries - 1); });
    });
  }
  function postJson(url, fields, tries) {
    return withRetry(function () {
      return fetch(url, { method: 'POST', body: ucForm(fields) }).then(function (r) {
        if (!r.ok) throw new Error(url + ' HTTP ' + r.status);
        return r.json();
      });
    }, tries || 3);
  }

  // Upload the untouched original. Resolves to its uuid. Not stored
  // permanently (Uploadcare drops unstored files after 24h) — only the
  // full-res crop made from it is kept.
  function startOriginalUpload(file) {
    var up = { done: false, failed: false, uuid: null, progress: 0 };
    var name = (file.name || 'original').replace(/[^\w.\-]+/g, '_');
    if (file.size < FULLRES_MULTIPART_MIN) {
      up.promise = withRetry(function () {
        return fetch('https://upload.uploadcare.com/base/', { method: 'POST',
          body: ucForm({ UPLOADCARE_PUB_KEY: cfg.publicKey, UPLOADCARE_STORE: '0', file: file }) })
          .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
          .then(function (d) { if (!d.file) throw new Error('no file'); return d.file; });
      }, 4);
    } else {
      up.promise = postJson('https://upload.uploadcare.com/multipart/start/', {
        UPLOADCARE_PUB_KEY: cfg.publicKey, UPLOADCARE_STORE: '0', filename: name,
        size: file.size, content_type: file.type || 'application/octet-stream'
      }, 4).then(function (st) {
        if (!st.parts || !st.uuid) throw new Error('multipart start');
        var i = 0;
        function next() {
          if (i >= st.parts.length) return Promise.resolve();
          var n = i++;
          var part = file.slice(n * FULLRES_CHUNK, Math.min((n + 1) * FULLRES_CHUNK, file.size));
          return withRetry(function () {   // each chunk retried on its own
            return fetch(st.parts[n], { method: 'PUT', body: part, headers: { 'Content-Type': 'application/octet-stream' } })
              .then(function (r) { if (!r.ok) throw new Error('chunk HTTP ' + r.status); });
          }, 4).then(function () { up.progress = Math.round(100 * i / st.parts.length); return next(); });
        }
        return next().then(function () {
          return postJson('https://upload.uploadcare.com/multipart/complete/', { UPLOADCARE_PUB_KEY: cfg.publicKey, uuid: st.uuid }, 4);
        }).then(function () { return st.uuid; });
      });
    }
    up.promise = up.promise.then(function (uuid) { up.done = true; up.uuid = uuid; up.progress = 100; return uuid; },
                                 function (err) { up.failed = true; console.warn('[ArloUploader] original upload failed — backup kept', err); throw err; });
    up.promise.catch(function () {}); // handled where it's awaited
    return up;
  }

  // Uploadcare operations that reproduce the customer's crop on the original.
  // Cropper rotates clockwise, Uploadcare counter-clockwise; Cropper's crop box
  // is in the rotated image's pixels, so rotate first, then crop.
  function fullResOps(fc) {
    var ops = '';
    var rot = ((fc.rotate % 360) + 360) % 360;
    if (rot) ops += '-/rotate/' + ((360 - rot) % 360) + '/';
    ops += '-/crop/' + fc.w + 'x' + fc.h + '/' + Math.max(0, fc.x) + ',' + Math.max(0, fc.y) + '/';
    if (fc.bw) ops += '-/grayscale/';
    if (fc.vivid) {
      // Uploadcare only allows colour adjustments after a resize, max 5000px.
      ops += fc.w >= fc.h ? '-/resize/' + Math.min(fc.w, FULLRES_MAX_RESIZE) + 'x/'
                          : '-/resize/x' + Math.min(fc.h, FULLRES_MAX_RESIZE) + '/';
      ops += fc.bw ? '-/contrast/20/' : '-/saturation/30/';   // = the preview's contrast(1.2) / saturate(1.3)
    }
    return ops + '-/format/jpeg/-/quality/best/';
  }

  // Ask Uploadcare to store the processed image as a new file. Resolves {uuid, w, h}.
  function copyProcessed(origUuid, ops, name) {
    return postJson('https://upload.uploadcare.com/from_url/', {
      pub_key: cfg.publicKey, store: '1', filename: name,
      source_url: 'https://ucarecdn.com/' + origUuid + '/' + ops
    }, 3).then(function (tk) {
      if (!tk.token) throw new Error('from_url: ' + JSON.stringify(tk));
      var started = Date.now();
      function poll() {
        return new Promise(function (r) { setTimeout(r, 1500); }).then(function () {
          return fetch('https://upload.uploadcare.com/from_url/status/?token=' + encodeURIComponent(tk.token)).then(function (r) { return r.json(); });
        }).then(function (st) {
          if (st.status === 'success') return { uuid: st.uuid || st.file_id, w: st.image_info && st.image_info.width, h: st.image_info && st.image_info.height };
          if (st.status === 'error') throw new Error('from_url error: ' + st.error);
          if (Date.now() - started > 180000) throw new Error('from_url timeout');
          return poll();
        });
      }
      return poll();
    });
  }

  // 16x16 greyscale fingerprint of an image URL (CORS-enabled CDN images).
  function fingerprint(url) {
    return new Promise(function (resolve, reject) {
      var im = new Image();
      im.crossOrigin = 'anonymous';
      im.onload = function () {
        try {
          var c = document.createElement('canvas'); c.width = c.height = 16;
          var x = c.getContext('2d'); x.drawImage(im, 0, 0, 16, 16);
          var d = x.getImageData(0, 0, 16, 16).data, out = [];
          for (var i = 0; i < d.length; i += 4) out.push(0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2]);
          resolve(out);
        } catch (e) { reject(e); }
      };
      im.onerror = function () { reject(new Error('fingerprint load')); };
      im.src = url;
    });
  }
  function looksSame(urlA, urlB) {
    return Promise.all([fingerprint(urlA), fingerprint(urlB)]).then(function (f) {
      var diff = 0;
      for (var i = 0; i < f[0].length; i++) diff += Math.abs(f[0][i] - f[1][i]);
      diff /= f[0].length;
      console.log('[ArloUploader] full-res check: mean difference', diff.toFixed(1));
      return diff < 18;  // same picture ≈ 0–8; wrong orientation/area ≈ 40+
    });
  }

  function upgradeSlot(i, fc) {
    var slot = slots[i];
    if (!slot) return;
    slot.fullState = 'pending';
    slot.fullSince = Date.now();
    var backupUuid = slot.uuid;
    fc.origUp.promise.then(function (origUuid) {
      return copyProcessed(origUuid, fullResOps(fc), 'photo-' + (i + 1) + '-fullres.jpg');
    }).then(function (res) {
      if (!res.uuid) throw new Error('no uuid');
      var thumb = '-/preview/64x64/';
      return looksSame('https://ucarecdn.com/' + backupUuid + '/' + thumb, 'https://ucarecdn.com/' + res.uuid + '/' + thumb)
        .then(function (same) { if (!same) throw new Error('full-res copy does not match the crop'); return res; });
    }).then(function (res) {
      // Only apply if this slot still holds the same photo (not cleared/re-cropped meanwhile)
      if (slots[i] !== slot || slot.uuid !== backupUuid) return;
      slot.fullUuid = res.uuid;
      slot.fullState = 'done';
      console.log('[ArloUploader] slot', i + 1, 'full-res', res.w + 'x' + res.h, '(backup kept:', backupUuid + ')');
      updateHiddenInput();
    }).catch(function (err) {
      console.warn('[ArloUploader] slot', i + 1, 'full-res not used — backup kept', err);
      if (slots[i] === slot) slot.fullState = 'failed';
    }).then(fullResSettled);
  }

  function fullResPending() {
    return slots.some(function (s) { return s.state === 'done' && s.fullState === 'pending'; });
  }
  function fullResProgress() {
    var p = slots.filter(function (s) { return s.state === 'done' && s.fullState === 'pending' && s.origUp; });
    if (!p.length) return 100;
    return Math.round(p.reduce(function (a, s) { return a + (s.origUp.done ? 95 : s.origUp.progress * 0.9); }, 0) / p.length);
  }
  var _atcRetry = null; // { btn, until, timer }
  function waitForFullResThenRetry(e) {
    var btn = e.target && e.target.closest && e.target.closest('[data-add-to-cart], [type="submit"]');
    if (!btn) { var f = document.querySelector('form[action*="/cart/add"]'); btn = f && f.querySelector('[type="submit"], [data-add-to-cart]'); }
    if (!_atcRetry) _atcRetry = { until: Date.now() + FULLRES_WAIT_MS };
    _atcRetry.btn = btn;
    showMsg('Uploading your full-quality photo (' + fullResProgress() + '%) — we\'ll add it to your cart in a moment…');
    clearInterval(_atcRetry.timer);
    _atcRetry.timer = setInterval(function () {
      if (!_atcRetry) return;
      if (Date.now() > _atcRetry.until) {
        // Waited long enough — use the backup crop for anything unfinished.
        slots.forEach(function (s) { if (s.fullState === 'pending') s.fullState = 'failed'; });
        console.warn('[ArloUploader] full-res still uploading after ' + FULLRES_WAIT_MS / 1000 + 's — using the backup crop');
        fullResSettled();
        return;
      }
      showMsg('Uploading your full-quality photo (' + fullResProgress() + '%) — we\'ll add it to your cart in a moment…');
    }, 1000);
  }
  function fullResSettled() {
    if (fullResPending() || !_atcRetry) return;
    var r = _atcRetry; _atcRetry = null;
    clearInterval(r.timer);
    showMsg('');
    updateHiddenInput();
    // Wait for the (multi-image) photo group link to be re-created with the
    // full-res files (max 10s), then press Add to Cart again.
    var started = Date.now();
    (function go() {
      var g = document.getElementById('arlo-group-url');
      if (g && !g.value && Date.now() - started < 10000) { setTimeout(go, 300); return; }
      if (r.btn) r.btn.click();
    })();
  }

  function slotThumbUrl(slot) {
    var u = slot.cdnUrl || '';
    return /^https:\/\/ucarecdn\.com\/[0-9a-f-]+\/$/i.test(u) ? u + '-/preview/600x600/' : u;
  }

  // ── Upload ───────────────────────────────────────────────────────────────────
  function uploadBlob(slotIdx, blob, fullCrop, brightened) {
    var previewUrl = URL.createObjectURL(blob);
    slots[slotIdx] = { state: 'loading', uuid: null, cdnUrl: null, origFile: slots[slotIdx].origFile, previewUrl: previewUrl,
                       origUp: slots[slotIdx].origUp, brightened: !!brightened };
    refreshSlot(slotIdx);

    var fd = new FormData();
    fd.append('UPLOADCARE_PUB_KEY', cfg.publicKey);
    fd.append('UPLOADCARE_STORE', '1');
    fd.append('file', blob, 'photo-' + (slotIdx + 1) + '.jpg');

    fetch('https://upload.uploadcare.com/base/', { method: 'POST', body: fd })
      .then(function (r) {
        if (!r.ok) throw new Error('Uploadcare returned HTTP ' + r.status);
        return r.json();
      })
      .then(function (data) {
        if (!data.file) throw new Error(JSON.stringify(data));
        URL.revokeObjectURL(slots[slotIdx].previewUrl);
        slots[slotIdx].state = 'done';
        slots[slotIdx].uuid = data.file;
        slots[slotIdx].cdnUrl = 'https://ucarecdn.com/' + data.file + '/';
        slots[slotIdx].previewUrl = null;
        refreshSlot(slotIdx);
        updateHiddenInput();
        showMsg('');
        if (fullCrop) upgradeSlot(slotIdx, fullCrop);
      })
      .catch(function (err) {
        console.error('[ArloUploader] upload error', err);
        URL.revokeObjectURL(slots[slotIdx].previewUrl);
        slots[slotIdx].previewUrl = null;
        slots[slotIdx].state = 'error';
        refreshSlot(slotIdx);
        showMsg('Upload failed: ' + err.message + '. Please check your Uploadcare account is active.');
      });
  }

  function updateHiddenInput() {
    // Single choke point: every slot change (upload done, clear, reset,
    // variant resize) funnels through here — persist the current state.
    savePersist();
    var done = slots.filter(function (s) { return s.state === 'done'; });
    var hidden = document.getElementById('arlo-upload-value');
    if (!hidden) return;
    hidden.disabled = false; // suppressNativeOptions() may have disabled this — re-enable so serialize() picks it up

    // Remove any previously injected per-photo inputs
    document.querySelectorAll('.arlo-photo-prop').forEach(function (el) { el.remove(); });

    if (!done.length) {
      hidden.value = '';
      hidden.name = 'properties[Custom image]';
      return;
    }

    // Hidden order note so production can see which photos were brightened
    var lifted = done.filter(function (s) { return s.brightened; });
    if (lifted.length) {
      var fb = hidden.closest('form') || document.querySelector('form[action*="/cart/add"]');
      if (fb) {
        var bi = document.createElement('input');
        bi.type = 'hidden'; bi.className = 'arlo-photo-prop';
        bi.name = 'properties[_arlo_brightened]';
        bi.value = done.length === 1 ? 'Yes' : done.map(function (s, n) { return s.brightened ? 'Photo ' + (n + 1) : ''; }).filter(Boolean).join(', ');
        fb.appendChild(bi);
      }
    }

    if (done.length === 1) {
      // Single image — use the existing hidden input directly
      hidden.name = 'properties[Custom image]';
      hidden.value = 'https://ucarecdn.com/' + (done[0].fullUuid || done[0].uuid) + '/';
      // Shape flag for cart display. Scoped to this exact photo's UUID via
      // _arlo_shape_uuids — without this, custom.js would apply the shape to
      // EVERY ucarecdn.com URL on the line item, including an unrelated
      // canvas-png personalised-message PNG on the same product.
      if (cfg.circle || isArchShape(cfg.shape)) {
        var f1 = hidden.closest('form') || document.querySelector('form[action*="/cart/add"]');
        if (f1) {
          if (cfg.circle) {
            var ci1 = document.createElement('input');
            ci1.type = 'hidden'; ci1.className = 'arlo-photo-prop';
            ci1.name = 'properties[_arlo_circle]'; ci1.value = 'true';
            f1.appendChild(ci1);
          }
          if (isArchShape(cfg.shape)) {
            var ai1 = document.createElement('input');
            ai1.type = 'hidden'; ai1.className = 'arlo-photo-prop';
            // Writes the actual shape id ('arch' or 'decal-arch') so downstream
            // cart rendering (assets/custom.js) knows which fixed ratio to apply.
            ai1.name = 'properties[_arlo_shape]'; ai1.value = cfg.shape;
            f1.appendChild(ai1);
          }
          var su1 = document.createElement('input');
          su1.type = 'hidden'; su1.className = 'arlo-photo-prop';
          su1.name = 'properties[_arlo_shape_uuids]'; su1.value = done[0].fullUuid || done[0].uuid;
          f1.appendChild(su1);
        }
      }
      return;
    }

    // Multiple images:
    // 1. Write each photo as its own visible property (for cart display)
    // 2. Create an Uploadcare group and store it as _Photo Group (for fulfillment)
    hidden.name = '_arlo_unused';
    hidden.value = '';

    var form = hidden.closest('form') || document.querySelector('form[action*="/cart/add"]');
    if (!form) return;

    // Individual display properties (synchronous)
    done.forEach(function (slot, i) {
      var inp = document.createElement('input');
      inp.type = 'hidden';
      inp.className = 'arlo-photo-prop';
      inp.name = 'properties[Photo ' + (i + 1) + ']';
      inp.value = 'https://ucarecdn.com/' + (slot.fullUuid || slot.uuid) + '/';
      form.appendChild(inp);
    });

    // Shape flag (hidden from cart UI via _ prefix, used for display styling).
    // Scoped to these exact photo UUIDs via _arlo_shape_uuids — see single-image
    // branch above for why the scoping matters.
    if (cfg.circle) {
      var ciMulti = document.createElement('input');
      ciMulti.type = 'hidden'; ciMulti.className = 'arlo-photo-prop';
      ciMulti.name = 'properties[_arlo_circle]'; ciMulti.value = 'true';
      form.appendChild(ciMulti);
    }
    if (isArchShape(cfg.shape)) {
      var aiMulti = document.createElement('input');
      aiMulti.type = 'hidden'; aiMulti.className = 'arlo-photo-prop';
      aiMulti.name = 'properties[_arlo_shape]'; aiMulti.value = cfg.shape;
      form.appendChild(aiMulti);
    }
    if (cfg.circle || isArchShape(cfg.shape)) {
      var suMulti = document.createElement('input');
      suMulti.type = 'hidden'; suMulti.className = 'arlo-photo-prop';
      suMulti.name = 'properties[_arlo_shape_uuids]';
      suMulti.value = done.map(function (s) { return s.fullUuid || s.uuid; }).join(',');
      form.appendChild(suMulti);
    }

    // Group URL property for fulfillment (async — hidden from cart UI via _ prefix)
    var groupInp = document.createElement('input');
    groupInp.type = 'hidden';
    groupInp.className = 'arlo-photo-prop';
    groupInp.id = 'arlo-group-url';
    groupInp.name = 'properties[_Photo Group]';
    groupInp.value = '';
    form.appendChild(groupInp);

    var uuids = done.map(function (s) { return s.fullUuid || s.uuid; });
    var params = new URLSearchParams();
    params.append('pub_key', cfg.publicKey);
    uuids.forEach(function (u, i) { params.append('files[' + i + ']', u + '/'); });

    fetch('https://upload.uploadcare.com/group/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: params.toString()
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data.id) {
          groupInp.value = 'https://ucarecdn.com/' + data.id + '~' + uuids.length + '/';
        }
      })
      .catch(function (err) { console.error('[ArloUploader] group error', err); });
  }

  // ── Slot management ──────────────────────────────────────────────────────────
  function clearSlot(i) {
    slots[i] = { state: 'empty', uuid: null, cdnUrl: null, origFile: null };
    refreshSlot(i);
    updateHiddenInput();
  }

  function resetAll() {
    for (var i = 0; i < slots.length; i++) {
      slots[i] = { state: 'empty', uuid: null, cdnUrl: null, origFile: null };
      refreshSlot(i);
    }
    updateHiddenInput();
    updateStickyBtn(0, cfg.maxImages);
  }

  window.ArloPhotoUploader = { reset: resetAll };

  // ── Resize slots when variant changes (via arlo-variant-uploader.js) ─────────
  function resizeTo(n) {
    if (n === slots.length) return;
    if (n > slots.length) {
      while (slots.length < n) {
        // If a variant change grows the grid and we have restored uploads
        // beyond the old size (e.g. 56-photo set restored into an 8-slot
        // default before the variant was re-selected), refill them here.
        var restoreU = _restoredUuids && _restoredUuids[slots.length];
        slots.push(restoreU ? restoredSlot(restoreU) : { state: 'empty', uuid: null, cdnUrl: null, origFile: null });
      }
    } else {
      slots = slots.slice(0, n);
    }
    cfg.maxImages = n;
    renderGrid();
    updateCount();
    updateHiddenInput();
  }

  // Watch #arlo-uploader for data-max-images attribute changes (set by variant uploader)
  function watchRoot(root) {
    var obs = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (m.attributeName === 'data-max-images') {
          var n = parseInt(root.getAttribute('data-max-images')) || 1;
          resizeTo(n);
        }
        if (m.attributeName === 'data-crop') {
          cfg.crop = root.getAttribute('data-crop') || '';
        }
        if (m.attributeName === 'data-min-px') {
          cfg.minPx = root.getAttribute('data-min-px') || '';
        }
      });
    });
    obs.observe(root, { attributes: true, attributeFilter: ['data-max-images', 'data-crop', 'data-min-px'] });
  }

  // ── Cart validation ──────────────────────────────────────────────────────────
  // Prevent add-to-cart if not all slots are filled
  function hookCartForm() {
    function cartBlocked(e) {
      var done = slots.filter(function (s) { return s.state === 'done'; }).length;
      var needed = slots.length;
      if (done < needed) {
        e.preventDefault();
        e.stopImmediatePropagation();
        showMsg(cfg.errorMessage || 'Please upload all ' + needed + ' photos before adding to cart.');
        var root = document.getElementById('arlo-uploader');
        if (root) root.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return true;
      }
      // Large prints: wait (max FULLRES_WAIT_MS after the click) for full-res
      // copies still being made, then add to cart automatically — with the
      // full-res files, or the backups for any that aren't ready.
      if (fullResPending()) {
        e.preventDefault();
        e.stopImmediatePropagation();
        waitForFullResThenRetry(e);
        return true;
      }
      // For multi-image: block until the group URL is ready (async fetch)
      var groupInp = document.getElementById('arlo-group-url');
      if (groupInp && !groupInp.value) {
        e.preventDefault();
        e.stopImmediatePropagation();
        showMsg('Almost ready — please try again in a moment.');
        return true;
      }
      return false;
    }

    // Main form submit (standard ATC button)
    var form = document.querySelector('form[action*="/cart/add"]');
    if (form) {
      form.addEventListener('submit', cartBlocked, true);
    }

    // Sticky bar + any [data-add-to-cart] button — theme JS intercepts clicks
    // so we must also run in the capture phase here
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-add-to-cart]');
      if (!btn) return;
      cartBlocked(e);
    }, true);
  }

  // ── Helpers ──────────────────────────────────────────────────────────────────
  function parseCropRatio(crop) {
    if (!crop) return NaN;
    var s = String(crop).replace(' minimum', '');
    // "W:H" ratio format — e.g. "1:1", "4:3", "3:2"
    if (s.indexOf(':') !== -1) {
      var rp = s.split(':');
      var rw = parseFloat(rp[0]), rh = parseFloat(rp[1]);
      if (rw && rh) return rw / rh;
    }
    // "WxH" pixel format — legacy, e.g. "1181x1181"
    if (s.indexOf('x') !== -1) {
      var pp = s.split('x');
      var pw = parseFloat(pp[0]), ph = parseFloat(pp[1]);
      if (pw && ph) return pw / ph;
    }
    return NaN;
  }

  function showMsg(text) {
    var el = document.getElementById('arlo-msg');
    if (!el) return;
    el.textContent = text;
    el.style.display = text ? 'block' : 'none';
  }

  // ── Init ─────────────────────────────────────────────────────────────────────
  function init() {
    var root = document.getElementById('arlo-uploader');
    if (!root) return;

    cfg = {
      publicKey:    root.dataset.publicKey    || '',
      maxImages:    parseInt(root.dataset.maxImages) || 1,
      crop:         root.dataset.crop         || '',
      minPx:        root.dataset.minPx        || '',
      printMm:      root.dataset.printMm      || '',
      enforceMinPx: root.dataset.enforceMinPx === 'true',
      circle:       root.dataset.circle === 'true',
      shape:        root.dataset.shape        || (root.dataset.circle === 'true' ? 'circle' : ''),
      overlay:      root.dataset.overlay      || '',
      errorMessage: root.dataset.error        || '',
      fullRes:      root.dataset.fullRes === 'true',
      darkLift:     root.dataset.darkLift === 'true'
    };

    slots = Array.from({ length: cfg.maxImages }, function () {
      return { state: 'empty', uuid: null, cdnUrl: null, origFile: null };
    });

    // Restore any uploads persisted from an interrupted session on this
    // product (see savePersist/loadPersist above). Done BEFORE mount so the
    // grid renders the restored thumbnails directly.
    var stored = loadPersist();
    var restoredCount = 0;
    if (stored) {
      _restoredUuids = stored;
      for (var ri = 0; ri < Math.min(stored.length, slots.length); ri++) {
        if (stored[ri]) { slots[ri] = restoredSlot(stored[ri]); restoredCount++; }
      }
    }

    mount(root);
    watchRoot(root);
    hookCartForm();
    updateStickyBtn(restoredCount, cfg.maxImages);
    if (restoredCount) {
      // Re-write the hidden property inputs (and kick off the async group
      // re-creation for multi-image) so add-to-cart works without re-uploading.
      updateHiddenInput();
    }
  }

  document.addEventListener('DOMContentLoaded', init);

})();
